import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "react-toastify";
import { errorLogger } from "@/utils/helper";
import {
  useSendPwtTransactionReportToEmail,
  useSendTransactionReportToEmail,
} from "./mutations";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const formSchema = z.object({
  email: z.string().email("Invalid email address"),
});

export type EmailType = z.infer<typeof formSchema>;

type MerchantFormProps = {
  onSubmit: (data: EmailType) => void;
  onClose: () => void;
  isPending: boolean;
};

function EmailForm({ onSubmit, onClose, isPending }: MerchantFormProps) {
  const form = useForm<EmailType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isPending} isLoading={isPending} type="submit" className="bg-primary-500 disabled:bg-primary-100">
            Submit
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

type EmailFormModalProps = {
  isOpen?: boolean;
  params?: any;
  onClose: () => void;
  type?: "card" | "pwt";
};

function TransactionReportEmailModal({
  isOpen,
  onClose,
  params,
  type,
}: EmailFormModalProps) {
  const { mutateAsync: sentTransaction, isPending: cardTransactionIsPending } =
    useSendTransactionReportToEmail();
  const { mutateAsync: sendPwt, isPending } =
    useSendPwtTransactionReportToEmail();

  async function handleSubmit(data: EmailType) {
    const emailData = {
      ...data,
      email: data.email.toLowerCase(),
    };

    if (type === "card") {
      try {
        await sentTransaction(
          { data: emailData, filter: params },
          {
            onSuccess() {
              toast.success("Email Sent Successfully");
              onClose();
            },
          }
        );
      } catch (error) {
        errorLogger(error);
      }
    } else {
      try {
        await sendPwt({ data: emailData, filter: params });
        toast.success("Email Sent Successfully");
        onClose();
      } catch (error) {
        errorLogger(error);
      }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-auto overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enter Email</DialogTitle>
          <p>
            Please enter the email address to receive the transaction report
          </p>
        </DialogHeader>
        <EmailForm
          onSubmit={handleSubmit}
          onClose={onClose}
          isPending={isPending || cardTransactionIsPending}
        />
      </DialogContent>
    </Dialog>
  );
}

export default TransactionReportEmailModal;
